import { createUserId, presenceColor } from '@kanvas/board-core';
import type { PublicUser } from './authApi';

export interface Identity {
  userId: string;
  name: string;
  color: string;
  avatar: string | null;
}

/**
 * Presence identity for a signed-in account — the name and colour on file
 * rather than a random one, so a teammate recognises who they are
 * collaborating with. The anonymous generator below stays the fallback for
 * guests opening a board link without ever logging in.
 */
export function identityFromAccount(user: PublicUser): Identity {
  return { userId: user.id, name: user.name, color: user.color, avatar: null };
}

const STORAGE_KEY = 'kanvas.identity.v1';

const ADJECTIVES = ['Быстрый', 'Спокойный', 'Яркий', 'Смелый', 'Тихий', 'Зоркий', 'Тёплый', 'Хитрый'];
const ANIMALS = ['Выдра', 'Сокол', 'Цапля', 'Рысь', 'Жук', 'Куница', 'Козерог', 'Сорокопут'];

function generateName(): string {
  const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]!;
  const b = ANIMALS[Math.floor(Math.random() * ANIMALS.length)]!;
  return `${a} ${b}`;
}

/**
 * Per-browser identity.
 *
 * Deliberately not an account: the board is usable the moment it loads, and a
 * real auth provider can later replace this by writing the same shape into
 * localStorage before the app boots.
 */
export function loadIdentity(): Identity {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<Identity>;
      if (parsed.userId && parsed.name) {
        return {
          userId: parsed.userId,
          name: parsed.name,
          color: parsed.color ?? presenceColor(parsed.userId),
          avatar: parsed.avatar ?? null,
        };
      }
    }
  } catch {
    // Private mode or a corrupt entry: fall through to a fresh identity.
  }

  const userId = createUserId();
  const identity: Identity = {
    userId,
    name: generateName(),
    color: presenceColor(userId),
    avatar: null,
  };
  saveIdentity(identity);
  return identity;
}

export function saveIdentity(identity: Identity): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // Non-fatal: the identity just will not survive a reload.
  }
}
